if (!customElements.get('image-zoom')) {
  customElements.define('image-zoom', class ImageZoom extends HTMLElement {
    constructor() {
      super();
      this.isZoomed = false;
      this.scale = parseFloat(this.getAttribute('data-scale')) || 2;

      // 事件绑定
      this.handleClick = this.handleClick.bind(this);
      this.handleMouseDown = this.handleMouseDown.bind(this);
      this.handleMouseMove = this.handleMouseMove.bind(this);
      this.handleMouseUp = this.handleMouseUp.bind(this);

      // 用于区分“点击”和“拖拽”的阈值
      this.DRAG_THRESHOLD = 5;

      // 记录偏移
      this.offsetX = 0;
      this.offsetY = 0;

      // 标记：拖拽完成后忽略一次 click
      this.ignoreClickOnce = false;

      // 最大可移动的距离
      this.maxTransX = 0;
      this.maxTransY = 0;

      // 失去焦点，还原
      this.addEventListener('focusout', this.zoomOut.bind(this));
    }

    connectedCallback() {
      this.image = this.querySelector('img');
      // 监听点击事件
      this.addEventListener('click', this.handleClick);
    }

    /**
     * 第一次点击放大；再次点击还原
     */
    handleClick(e) {
      // 若刚刚拖拽结束，则忽略一次点击
      if (this.ignoreClickOnce) {
        this.ignoreClickOnce = false;
        return;
      }

      // 若在拖拽过程中松开鼠标，容易触发 click
      // 这里根据 isDragging 判断
      if (this.isDragging) return;

      if (!this.isZoomed) {
        this.zoomIn();
      } else {
        this.zoomOut();
      }
    }

    /**
     * 放大逻辑
     */
    zoomIn() {
      this.isZoomed = true;
      // 复位光标
      this.style.cursor = 'grab';
      // 先做放大动画
      this.image.style.transition = 'transform 300ms ease';
      this.image.style.transform = `scale(${this.scale}) translate(0, 0)`;

      // 动画结束后再解绑 transition，并允许拖拽
      this.image.addEventListener('transitionend', () => {
        this.image.style.transition = '';
        this.addEventListener('mousedown', this.handleMouseDown);
        this.addEventListener('pointerdown', this.handleMouseDown);
        this.calculateMaxTranslate();
      }, { once: true });
    }

    /**
     * 还原逻辑
     */
    zoomOut() {
      this.isZoomed = false;
      this.style.cursor = 'zoom-in';
      // 解绑拖拽事件
      this.removeEventListener('mousedown', this.handleMouseDown);
      this.removeEventListener('mousemove', this.handleMouseMove);
      document.removeEventListener('mouseup', this.handleMouseUp);

      // 恢复原样
      this.image.style.transition = 'transform 300ms ease';
      this.image.style.transform = 'scale(1) translate(0, 0)';

      // 同时将偏移重置
      this.offsetX = 0;
      this.offsetY = 0;
    }

    /**
     * 计算最大可拖拽的范围
     */
    calculateMaxTranslate() {
      // 获取容器和图片的尺寸
      const rect = this.getBoundingClientRect(); // 容器的尺寸
      const scaledImageRect = this.image.getBoundingClientRect(); // 放大后的图片尺寸

      const scaledWidth = scaledImageRect.width; // 放大后的图片宽度
      const scaledHeight = scaledImageRect.height; // 放大后的图片高度

      // 计算最大可拖拽的范围
      this.maxTransX = Math.max((scaledWidth - rect.width) / 2, 0);
      this.maxTransY = Math.max((scaledHeight - rect.height) / 2, 0);
    }

    /**
     * 鼠标按下，准备拖拽
     */
    handleMouseDown(e) {
      if (!this.isZoomed) return;
      e.preventDefault();

      this.isDragging = false;
      // 记录鼠标按下时的位置（相对于 document）
      this.startX = e.clientX || e.touches[0].clientX;
      this.startY = e.clientY || e.touches[0].clientY;

      // 记录“拖拽开始”时的偏移，用于后续累加
      this.startOffsetX = this.offsetX;
      this.startOffsetY = this.offsetY;

      this.style.cursor = 'grabbing';

      // 监听移动和松开
      this.addEventListener('mousemove', this.handleMouseMove);
      this.addEventListener('pointermove', this.handleMouseMove);
      document.addEventListener('mouseup', this.handleMouseUp);
      document.addEventListener('pointerup', this.handleMouseUp);
    }

    /**
     * 鼠标移动，执行拖拽
     */
    handleMouseMove(e) {
      e.preventDefault();

      // 计算本次移动与初始点的差值
      const currentX = e.clientX || e.touches[0].clientX;
      const currentY = e.clientY || e.touches[0].clientY;
      const deltaX = currentX - this.startX;
      const deltaY = currentY - this.startY;

      // 若超过一定阈值才判定为拖拽，避免轻微点击就被当成拖拽
      if (!this.isDragging && Math.hypot(deltaX, deltaY) > this.DRAG_THRESHOLD) {
        this.isDragging = true;
      }

      if (this.isDragging) {
        // 基于之前的偏移 + 本次位移，得到新的偏移，并限制在最大移动范围内
        let newOffsetX = this.startOffsetX + deltaX;
        let newOffsetY = this.startOffsetY + deltaY;

        // 限制在最大可移动范围内
        newOffsetX = Math.max(Math.min(newOffsetX, this.maxTransX), -this.maxTransX);
        newOffsetY = Math.max(Math.min(newOffsetY, this.maxTransY), -this.maxTransY);

        // 更新偏移
        this.offsetX = newOffsetX;
        this.offsetY = newOffsetY;

        // 更新 transform
        this.image.style.transform = `scale(${this.scale}) translate(${this.offsetX}px, ${this.offsetY}px)`;
      }
    }

    /**
     * 鼠标松开，结束拖拽
     */
    handleMouseUp(e) {
      this.style.cursor = 'grab';

      // 移除移动监听
      this.removeEventListener('mousemove', this.handleMouseMove);
      this.removeEventListener('pointermove', this.handleMouseMove);
      document.removeEventListener('mouseup', this.handleMouseUp);
      document.removeEventListener('pointerup', this.handleMouseUp);

      // 若确实发生了拖拽，可能会触发一次多余的 click，这里标记忽略
      if (this.isDragging) {
        // 避免一次多余点击
        this.ignoreClickOnce = true;
      }

      this.isDragging = false;
    }
  });
}